import { useState, useMemo, Fragment, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Typography, Button, Grid, Card, Tooltip } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CustomAvatar } from '../../../../../reusable-components/custom-avatar';
import { useSelector, useDispatch } from '../../../../../redux/store';
import { useSnackbar } from '../../../../../reusable-components/snackbar';
import CartItemView from './CartItemView';
import { deselectAllItems, postSubmitOrder, selectAllItems } from '../../../../../redux/slices/rentals';
import { getAllCartsItemsCount } from '../../../../../redux/slices/orderSheet';
import { useScopeCheck } from '../../../../../reusable-components/scopes';

RentalCartView.propTypes = {
};

const getPunchoutClassificationMessage = (cartItems) => {
  const hasMissingSubcategory = cartItems.some((item) => item.isPunchoutCart && item.subcategoryId <= 0);

  if (hasMissingSubcategory) return 'Missing subcategory.';
  return '';
};

export default function RentalCartView() {
  const { facilityId = '' } = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const {
    data: { cart },
  } = useSelector((state) => state.rentals);
  const { enqueueSnackbar } = useSnackbar();
  const hasSubmitAccess = useScopeCheck(['Rental-Order-Submit'], true)
  const hasPunchoutAccess = useScopeCheck(['Rental-Order-Punchout'], true)
  const [isPunchoutCart, setIsPunchoutCart] = useState(); 
  const newCart = useMemo(() => {
    if (facilityId != null) {
      return cart.filter((x) => x.facilityId === Number(facilityId));
    }
    return [];
  }, [cart, facilityId]);
  const [processingOrder, setProcessingOrder] = useState(false);

  const [checkout, setCheckout] = useState(false);
  const [showCartEditOptions, setShowCartEditOptions] = useState(true);
  const [showQuantity, setShowQuantity] = useState(false);

  const cartByResidentArrays = useMemo(() => {
    const cartByResident = cart.reduce((acc, item) => {
      if (!acc[item.residentId]) {
        acc[item.residentId] = [];
      }
      acc[item.residentId].push(item);
      return acc;
    }, {});
    return Object.values(cartByResident);
  }, [cart]);
  const handleDeselect = (residentId) => {
    dispatch(deselectAllItems(residentId));
  };
   const handleSelect = (residentId) => {
    dispatch(selectAllItems(residentId));
  };

  const processOrder = () => {
    if (!cart.some((item) => item.isSelected)) {
      enqueueSnackbar('Please select items to checkout.', { variant: 'warning' });
      return;
    }
    setCheckout(true);
    setShowCartEditOptions(false);
    setShowQuantity(true);
  }
  const handleSubmitOrder = async () => {
    setProcessingOrder(true);
    const selectedItem = cart.find(item => item.isSelected);
    const residentCartItems = cart.filter(item => item.cartId === selectedItem.cartId);
    const response = await dispatch(postSubmitOrder(residentCartItems, residentCartItems.some(item => item.isPunchoutCart)));
    if (response?.status === 200 && response?.data?.hasApproveAccess === true) {
      dispatch(getAllCartsItemsCount())
      navigate(`/dashboard/rentals/rental-order/${response?.data?.orderId}/pending`);
    }
    else if (response?.status === 200 && response?.data?.hasApproveAccess === false) {
      navigate(`/dashboard/rentals/new-rental/rental-order-summary/?orderNumber=${response.data.orderNumber}`);   
    }
    setProcessingOrder(false);
  };

  useEffect(() => {
    if (cart.length > 0) {
      setIsPunchoutCart(cart.some(item => item.itemId === 0));
    }
  }, [cart]);

  useEffect(() => {
    if (cart.length > 0) {
      setIsPunchoutCart(cart.some(item => item.itemId === 0));
    }
  }, [cart]);

  const getNameById = (id) => cart.find((r) => r.residentId === id);

  const hasMultipleResidents = (cart) => {
    const selectedItems = cart.filter((item) => item.isSelected);
    if (selectedItems.length === 0) return true;
    const firstSelectedResidentId = selectedItems[0].residentId;
    return selectedItems.some((item) => item.residentId !== firstSelectedResidentId);
  };
  const isCheckoutDisabled = hasMultipleResidents(cart);

  return (
    <div>
      <Typography
        variant="subtitle1"
        sx={{ ml: 6, mt: 1, mb: 2, color: '#8dc63f', fontWeight: 'bold', fontSize: '15px' }}
      >
        Shopping Carts
      </Typography>

      <Grid container direction="row">
        <Grid item sx={{ width: '75%' }}>
          {cartByResidentArrays.map((resident) => {
              const residentData = getNameById(resident[0].residentId);
              const cartResident = `${resident[0].residentFirstName} ${resident[0].residentLastName}`;
              const allSelected = resident.every((item) => item.isSelected);
              const punchoutClassificationMessage = getPunchoutClassificationMessage(resident);
                return (
                <Fragment key={resident[0].residentId}>
                  {((checkout && resident.some((item) => item.isSelected === true)) || !checkout) && (
                  <Card
                    sx={{ width: '90%', display: 'inline-flex', flexDirection: 'column', mb: 4, padding: 3, ml: 4 }}
                  >
                    <Grid container direction="row" justifyContent="space-between" alignItems="center">
                    <Grid item container direction="row" spacing={1} alignItems="center">
                      <Grid item>
                      <CustomAvatar
                        name={cartResident}
                        size="large"
                        showTooltip={false}
                        sx={{
                        height: 40,
                        width: 40,
                        fontSize: 40 / 2,
                        }}
                      />
                      </Grid>
                      <Grid item>
                      <Typography variant="subtitle1" sx={{ color: '#707070', fontWeight: 'bold' }}>
                        {cartResident}
                      </Typography>
                      </Grid>
                      <Grid item sx={{ marginLeft: 'auto' }}>
                      <Typography variant="subtitle1" sx={{ color: '#8cc53f' }}>
                        Total: ${resident.reduce((acc, item) => acc + item.price * item.quantity, 0).toFixed(2)}
                      </Typography>
                      </Grid>
                    </Grid>
                    <Grid item sx={{ marginLeft: 'auto' }}> 
                      {showCartEditOptions && ((!isPunchoutCart && hasSubmitAccess) || (isPunchoutCart && hasPunchoutAccess)) && (
                      <Tooltip title={punchoutClassificationMessage}>
                        <span>
                          <Typography
                            size="small"
                            color="primary"
                            sx={{
                            mb: 1.5,
                            marginLeft: 'auto',
                            textDecoration: 'underline',
                            fontSize: '15px',
                            color: '#4ca5e7',
                            opacity: punchoutClassificationMessage ? 0.5 : 1,
                            '&:hover': {
                              cursor: punchoutClassificationMessage ? 'not-allowed' : 'pointer',
                            },
                            }}
                            onClick={() => {
                            if (punchoutClassificationMessage) return;
                            if (!cart.some((item) => item.isSelected)) {
                              enqueueSnackbar('Please select items to checkout.', { variant: 'warning' });
                              return;
                            }
                              navigate(`/dashboard/rentals/new-rental/rental-checkout/${residentData.residentId}`);
                            }
                          }
                          >
                            Checkout {cartResident}
                          </Typography>
                        </span>
                      </Tooltip>
                      )}
                    </Grid>
                    </Grid>
                    <Grid item>
                    {showCartEditOptions && hasSubmitAccess && (
                      <Typography
                      variant="subtitle2"
                      sx={{
                        color: '#6d6e71',
                        textDecoration: 'underline',
                        '&:hover': {
                        cursor: 'pointer',
                        },
                      }}
                      onClick={() => {
                        if(allSelected) handleDeselect(residentData.residentId);
                        else {handleSelect(residentData.residentId)};
                      }}
                      >
                      {allSelected ? 'Deselect all' : 'Select all'}
                      </Typography>
                    )}
                    </Grid>
                    {resident.map((cartItem, ind) => (
                    <Fragment key={cartItem.cartItemId}>
                      {((checkout && cartItem.isSelected === true) || !checkout) && (
                      <CartItemView
                        cartItem={cartItem}
                        showDivider={resident.length - 1 !== ind}
                        showCartEditOptions={showCartEditOptions && hasSubmitAccess}
                        showQuantity={showQuantity}
                      />
                      )}
                    </Fragment>
                    ))}
                  </Card>
                  )}
                </Fragment>
                );
          })}
        </Grid>
        <Grid item sx={{ width: '25%' }}>
          <Grid container direction="column" sx={{ maxWidth: '70%', alignItems: 'center' }}>
            <Card sx={{ padding: 3, alignItems: 'center' }}>
              <Grid item sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle1" sx={{ display: 'inline-flex', textAlign: 'center', color: '#6d6e71' }}>
                  Subtotal: (
                  {cart.length
                    ? (() => {
                        const selectedItems = cart.filter((item) => item.isSelected);
                        const selectedCount = selectedItems.reduce((acc, item) => acc + item.quantity, 0);
                        return `${selectedCount} Item${selectedCount !== 1 ? 's' : ''}`;
                      })()
                    : 'Cart is empty'}
                  ) ${cart.filter(item => item.isSelected).reduce((acc, item) => acc + (item.price * item.quantity), 0).toFixed(2)}
                </Typography>
              </Grid>
              <Grid item sx={{ textAlign: 'center' }}>
              {hasSubmitAccess &&
               ( !checkout ? (
                  <Tooltip title={isCheckoutDisabled ? 'Select items for only one resident to proceed.' : ''}>
                    <Button
                     sx={{
                        '&:hover': {
                          cursor: 'pointer',
                        },
                        display: 'inline-flex',
                        justifyContent: 'center',
                        mt: 2,
                      }}
                      color="primary"
                      size="small"
                      variant="contained"
                      disabled={isCheckoutDisabled}
                      onClick={processOrder}
                    >
                      Proceed To Checkout
                    </Button>
               </Tooltip>
                ) : (
                  <LoadingButton
                    sx={{
                      '&:hover': {
                        cursor: 'pointer',
                      },
                      display: 'inline-flex',
                      justifyContent: 'center',
                      mt: 2,
                    }}
                    variant="contained"
                    loading={processingOrder}
                    onClick={handleSubmitOrder}
                  >
                    Submit Order for Approval
                  </LoadingButton>
                ))
              }
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
}
